import { useEffect, useRef, useState } from 'react'
import { useChat } from '../contexts/ChatContext'
import { marked } from 'marked'
import EmptyState from './EmptyState'

// Configure marked to open links in new tabs
const renderer = new marked.Renderer()
const originalLinkRenderer = renderer.link
renderer.link = function(href, title, text) {
  const linkHtml = originalLinkRenderer.call(this, href, title, text)
  return linkHtml.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ')
}

marked.setOptions({
  renderer: renderer,
  breaks: true,
  gfm: true
})

function Message({ message, onCopy, isCopied }) {
  const isAssistant = message.role === 'assistant'

  return (
    <div className="animate-fade-in">
      <div
        className={`
          relative inline-block px-4 py-3 break-words group
          ${isAssistant
            ? 'w-full max-w-full bg-card text-card-foreground border border-border/50 rounded-2xl shadow-sm'
            : 'max-w-[90%] sm:max-w-[80%] md:max-w-[75%] lg:max-w-[70%] bg-primary text-primary-foreground rounded-3xl shadow-md ml-auto mr-0'
          }
        `}
      >
        {isAssistant && (
          <button
            onClick={onCopy}
            className="absolute top-2 left-2 p-1.5 rounded-lg bg-background/80 hover:bg-background border border-border/50 hover:border-border transition-all duration-150 opacity-0 hover:opacity-100 focus:opacity-100 group-hover:opacity-100"
            title="نسخ"
          >
            {isCopied ? (
              <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
        )}
        {isAssistant ? (
          <div
            className="markdown-content"
            dangerouslySetInnerHTML={{ __html: marked.parse(message.content) }}
          />
        ) : (
          <p className="whitespace-pre-wrap text-[15px] leading-[1.6]">{message.content}</p>
        )}
      </div>
    </div>
  )
}

export default function MessagesArea() {
  const { messages, showTyping } = useChat()
  const messagesEndRef = useRef(null)
  const [copiedIndex, setCopiedIndex] = useState(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, showTyping])

  const handleCopy = async (message, index) => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopiedIndex(index)
      setTimeout(() => setCopiedIndex(null), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const hasMessages = messages.length > 0

  return (
    <div className="h-full overflow-y-auto px-4 md:px-8 lg:px-12">
      <div className="max-w-5xl mx-auto py-6">
        {!hasMessages ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-4">
            {messages.map((message, index) => (
              <Message
                key={index}
                message={message}
                onCopy={() => handleCopy(message, index)}
                isCopied={copiedIndex === index}
              />
            ))}
            {showTyping && (
              <div className="text-muted-foreground italic text-right animate-pulse-soft text-sm leading-relaxed">
                من أجل إجابة قانونية أدق، قد يستغرق "AIlex" بعض الوقت للتفكير العميق والبحث
                <br />
                <span className="font-medium">جارٍ كتابة الإجابة…</span>
                <br />
                <small className="text-muted-foreground/70">تنويه: قد يُخطئ AIlex أحيانًا</small>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  )
}
